import { UIIcon } from "@dropbox/dig-icons";
import { AddCircleLine } from "@dropbox/dig-icons/dist/mjs/assets";
import { PAP_Click_AddSourcesButton } from "@mirage/analytics/events/types/click_add_sources_button";
import { ContextInputMessages } from "@mirage/mosaics/Chat/components/chat/ContextInputMessages";
import type { ClickableEmptyChatInstructionsOption } from "@mirage/mosaics/Chat/components/chat/instructions/EmptyChatInstructions";
import { EmptyChatInstructions } from "@mirage/mosaics/Chat/components/chat/instructions/EmptyChatInstructions";
import { ChatGrid } from "@mirage/mosaics/Chat/components/layouts/ChatPageLayout";
import { useTemplateSettingsModal } from "@mirage/mosaics/ComposeAssistant/components/settings/useTemplateSettingsModal";
import { SidePanelContainer } from "@mirage/mosaics/ComposeAssistant/components/side-panel/SidePanelContainer";
import { useWorkflowAgentUI } from "@mirage/mosaics/ComposeAssistant/components/workflow-agent/useWorkflowAgentUI";
import { ComposeEditorPane } from "@mirage/mosaics/ComposeAssistant/containers/ComposeEditorPane";
import { CategorySearchAccordionWrapper } from "@mirage/mosaics/SearchPage/CategorySearchAccordion/CategorySearchAccordionWrapper";
import {
  appendManualMessages,
  createNewConversation,
  postUserMessage,
  updateConversationDraft,
} from "@mirage/service-conversation";
import useConversation from "@mirage/service-conversation/hooks/useConversation";
import { useFeatureFlagValue } from "@mirage/service-experimentation/useFeatureFlagValue";
import { convertFeatureValueToBool } from "@mirage/service-experimentation/util";
import { useDashActionSurface } from "@mirage/shared/hooks/useDashActionSurface";
import { IconButtonWithTooltip } from "@mirage/shared/icons/IconButtonWithTooltip";
import { usePrevious } from "@mirage/shared/util/hooks";
import i18n from "@mirage/translations";
import { RoutePath } from "@mirage/webapp/routeTypes";
import { useAtom } from "jotai";
import {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import {
  Navigate,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { useConversationDraftConfig } from "../hooks/useConversationDraftConfig";
import type { UserMessageOptions } from "./ConversationChat";
import { ConversationChat } from "./ConversationChat";
import { ConversationList } from "./ConversationList";

import { PAP_Click_DashChatAction } from "@mirage/analytics/events/types/click_dash_chat_action";
import type { SourcesContentCache } from "@mirage/mosaics/Chat/data/ComposeSourcesCache";
import type { ChatSource } from "@mirage/mosaics/Chat/types";
import {
  urlForConversationWithID,
  useConversationIdFromSearchParams,
} from "@mirage/mosaics/Chat/utils/navigation";
import { useAssistTemplatesContext } from "@mirage/mosaics/ComposeAssistant/data/AssistTemplatesContext";
import { useComposeAnalyticsContext } from "@mirage/mosaics/ComposeAssistant/data/ComposeAnalyticsContext";
import { useComposeCurrentSessionContext } from "@mirage/mosaics/ComposeAssistant/data/current-session/ComposeCurrentSessionContext";
import {
  conversationPageLoadingAtom,
  useConversationPageLoadingStates,
} from "@mirage/mosaics/ConversationAssistant/hooks/useConversationPageLoading";
import { useCloseOmniboxSearchSession } from "@mirage/mosaics/Omnibox/hooks/useCloseOmniboxSearchSession";
import {
  getDashActionSurfaceComponentLoggingSource,
  useConversationRouterState,
} from "@mirage/service-conversation/hooks/useConversationRouterState";
import { useManuallyAddedSources } from "@mirage/service-conversation/hooks/useManuallyAddedSources";
import type {
  ModelId,
  NewUserMessage,
} from "@mirage/service-conversation/types";
import type { ConversationRouterState } from "@mirage/service-conversation/utils/buildConversationRouterState";
import { convertChatSourceToDocumentInfo } from "@mirage/service-conversation/utils/convertChatSourceToDocumentInfo";
import type { ComposeAssistantConversationMessage } from "@mirage/shared/compose/assist-message";
import type { ComposeAssistantDraftConfig } from "@mirage/shared/compose/compose-session";
import { DashSpinnerAutofitContainer } from "@mirage/shared/dash-spinner/DashSpinnerAutofitContainer";
import { ScenarioBackButton } from "../components/ScenarioBackButton";
import { StackChatPanel } from "../components/stack-chat/StackChatPanel";
import { useStackChatDetails } from "../components/stack-chat/useStackChatDetails";

import { useConversationProgress } from "../hooks/useConversationProgress";

interface ConversationAssistantPageContentProps {
  variant?: "default" | "condensed";
}

export const ConversationAssistantPageContent = memo(
  ({ variant = "default" }: ConversationAssistantPageContentProps) => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { conversationId, resetNewConversation, isConversationLoading } =
      useConversationId(searchParams);
    const conversation = useConversation(conversationId);
    useConversationPageLoadingStates(conversation, conversationId);
    const [isConversationPageLoading, setIsConversationPageLoading] = useAtom(
      conversationPageLoadingAtom,
    );
    const [rightPaneExpanded, setRightPaneExpanded] = useState<boolean>(
      !!conversation?.markdownDraft,
    );
    const [hasCategoryResults, setHasCategoryResults] = useState(false);
    const [isAddSourceModalOpen, setIsAddSourceModalOpen] = useState(false);
    const { closeOmniboxSearchSessionIfExists } =
      useCloseOmniboxSearchSession();
    const { currentSessionID } = useComposeCurrentSessionContext();
    const { logComposeEvent } = useComposeAnalyticsContext({
      actionSurfaceComponent: "compose_chat_pane",
      currentSessionID,
    });
    const { dashActionSurface } = useDashActionSurface();
    const { state } = useLocation();
    const { agentId, model } = state || {};
    const { manuallyAddedSources, setManuallyAddedSources } =
      useManuallyAddedSources();
    const attachments = useMemo(
      () => manuallyAddedSources.map(convertChatSourceToDocumentInfo),
      [manuallyAddedSources],
    );
    const { progressString, isWaitingForResponse } = useConversationProgress(
      setManuallyAddedSources,
      conversationId,
    );
    const stackChatDetails = useStackChatDetails(state, conversation);
    const [userMessageOptions, setUserMessageOptions] = useState<
      UserMessageOptions | undefined
    >();
    const updateDraft = useCallback(
      (markdownContent: string) => {
        if (conversationId) {
          updateConversationDraft(conversationId, markdownContent);
        }
      },
      [conversationId],
    );

    const [selectedModel, setSelectedModel] = useState<ModelId>(model);

    const {
      selectedTemplate,
      setSelectedTemplate,
      selectedVoiceID,
      setSelectedVoiceID,
      agentPayload,
      onPostMessageWithVoice,
      onPostMessageWithTemplate,
      onTemplateChange,
      resetDraftConfigState,
    } = useConversationDraftConfig({
      conversationId,
      markdownDraft: conversation?.markdownDraft,
      model: selectedModel,
      setManuallyAddedSources,
      writeAgentPayload: conversation?.writeAgentPayload,
      setUserMessageOptions,
    });
    useConversationRouterState({
      agentPayload,
      chatSessionId: currentSessionID,
      conversationId,
      setSelectedVoiceID,
      setSelectedTemplate,
      setUserMessageOptions,
    });
    const { templates, saveTemplate, deleteTemplate } =
      useAssistTemplatesContext();

    const { messagesToDisplay, templateSelectedMessage } = useMemo(() => {
      if (!conversation?.messages?.length) {
        return { filteredMessages: [], templateSelectedMessage: undefined };
      }
      const firstMessage = conversation.messages[0];
      const isTemplateMessage =
        firstMessage?.messageType?.case === "userMessage" &&
        firstMessage.messageType.value.llmIgnore;

      if (isTemplateMessage) {
        // Convert to the format expected by ContextInputMessages. Doesn't matter
        // that it's in the old format since this is just for display
        const composeMessage = {
          type: "message",
          role: "user",
          text: firstMessage.messageType.value?.text || "",
          actionContext: { type: "template_selected" },
        } as ComposeAssistantConversationMessage;
        return {
          messagesToDisplay: conversation.messages.slice(1),
          templateSelectedMessage: composeMessage,
        };
      }

      return {
        messagesToDisplay: conversation.messages,
        templateSelectedMessage: undefined,
      };
    }, [conversation?.messages]);

    const prevMarkdownDraft = usePrevious(conversation?.markdownDraft);
    useEffect(() => {
      if (conversation?.markdownDraft && !prevMarkdownDraft) {
        setRightPaneExpanded(true);
      }
    }, [conversation?.markdownDraft, prevMarkdownDraft]);

    useLayoutEffect(() => {
      closeOmniboxSearchSessionIfExists("assist", {
        conversationId: conversation?.serverConversationId,
      });
    }, [conversation?.serverConversationId, closeOmniboxSearchSessionIfExists]);

    const workflowAgentUI = useWorkflowAgentUI();

    const handleClickAdd = useCallback(() => {
      setIsAddSourceModalOpen(true);
      logComposeEvent(
        PAP_Click_AddSourcesButton({
          actionType: "chat_pane",
          actionSurface: dashActionSurface,
          dashActionSurface,
          actionSurfaceComponent: "compose_chat_pane",
          featureLine: "assist",
        }),
      );
    }, [dashActionSurface, logComposeEvent, setIsAddSourceModalOpen]);

    const isConversationAssistantEnabled = convertFeatureValueToBool(
      useFeatureFlagValue("dash_2025_02_11_conversation_assistant"),
    );
    const isCategoriesEnabled =
      useFeatureFlagValue("dash_2025_03_05_serp_categories") === "V1";

    const firstMessageFromUser = useMemo(
      () =>
        conversation?.messages?.find(
          (message) =>
            message.messageType?.case === "userMessage" &&
            !message.messageType?.value.llmIgnore,
        )?.messageType?.value?.text,
      [conversation?.messages],
    );
    const [emptyChatOption, setEmptyChatOption] = useState<
      ClickableEmptyChatInstructionsOption | undefined
    >(undefined);
    const showEmptyChatInstructions =
      !isConversationPageLoading &&
      !isConversationLoading &&
      conversation !== undefined &&
      !conversation?.messages?.length &&
      !emptyChatOption &&
      attachments.length === 0;
    const emptySources: ChatSource[] = [];
    const emptySourcesContentCache: SourcesContentCache = {};
    const emptyDraftConfig: ComposeAssistantDraftConfig = {};

    const onCompleteInputs = useCallback(() => {
      appendManualMessages([
        {
          conversationId: conversationId || "",
          message: i18n.t("compose_assistant_message_partial_match"),
          llmIgnore: true,
          messageType: "assistantMessage",
          source:
            getDashActionSurfaceComponentLoggingSource("compose_chat_pane"),
        },
      ]);
    }, [conversationId]);

    const handleCloseAddSourcesModal = useCallback(() => {
      // If a template is selected and there are manually added source, auto-trigger a response
      if (
        templateSelectedMessage &&
        manuallyAddedSources.length > 0 &&
        conversation?.messages?.length === 1
      ) {
        const source =
          getDashActionSurfaceComponentLoggingSource("compose_chat_pane");
        const isStartFromScratch = selectedTemplate === null;
        const rephrased = isStartFromScratch
          ? ""
          : "Use the template to generate a draft using content from the provided source documents";
        const messagePayload: NewUserMessage = {
          message: "",
          rephrased,
          conversationId: conversationId || "",
          agentPayload: JSON.stringify(agentPayload),
          attachments,
          agentIds: ["write"],
          messageType: "userMessage",
          source,
        };
        if (isStartFromScratch) {
          appendManualMessages([
            {
              ...messagePayload,
            },
            {
              conversationId: conversationId || "",
              message: i18n.t("compose_assistant_message_partial_match"),
              llmIgnore: true,
              messageType: "assistantMessage",
              source,
            },
          ]);
          setUserMessageOptions({ skipSearch: true, agentIds: ["write"] }); // assume that the first message after start from scratch + attachments won't use search
        } else {
          postUserMessage({ ...messagePayload, skipSearch: true }); // for any actual template, trigger a request immediately
        }
        setManuallyAddedSources([]);
      }
      setIsAddSourceModalOpen(false);
    }, [
      conversationId,
      manuallyAddedSources,
      templateSelectedMessage,
      conversation?.messages,
      agentPayload,
      attachments,
      setManuallyAddedSources,
      selectedTemplate,
    ]);

    const { setOpenTemplateSettings, templateSettingsModal } =
      useTemplateSettingsModal({
        templates: templates || [],
        currentTemplateID: selectedTemplate?.id || null,
        saveTemplate: async (template) => {
          const id = await saveTemplate(template);
          return id || "";
        },
        deleteTemplate,
        onTemplateChange,
        postUpdateDraftWithTemplate: onPostMessageWithTemplate,
        logTemplatesPaneEvent: () => {},
      });

    const handleExploreTemplates = useCallback(() => {
      setOpenTemplateSettings({ type: "empty_state" });
      logComposeEvent(
        PAP_Click_DashChatAction({
          featureLine: "assist",
          dashChatAction: "explore_templates",
        }),
      );
    }, [setOpenTemplateSettings]);

    const startingMessageNode = showEmptyChatInstructions ? (
      <EmptyChatInstructions
        chatWithDashEnabled
        onClickOption={(opt: ClickableEmptyChatInstructionsOption) => {
          setEmptyChatOption(opt);
          if (opt === "chat_with_dash") {
            if (!conversationId) {
              throw new Error(
                "Attempting to set option without conversationId",
              );
            }
            appendManualMessages([
              {
                conversationId: conversationId,
                message: i18n.t("assistant_initial_generic_prompt_message"),
                llmIgnore: true,
                messageType: "assistantMessage",
                source:
                  getDashActionSurfaceComponentLoggingSource(
                    "compose_chat_pane",
                  ),
              },
            ]);
          } else if (opt === "write") {
            setUserMessageOptions({ agentIds: ["write"] });
          }
        }}
      />
    ) : isConversationPageLoading && !conversation?.messages?.length ? (
      // transition for router redirects is fast enough that showing a spinner just shows another flash
      // instead of preventing the flash of the empty state content. loading state for hydration of an
      // existing conversation is already handled at the page level by isConversationLoading
      <></>
    ) : (
      <>
        {emptyChatOption === "write" && (
          <ContextInputMessages
            sources={emptySources}
            sourcesContentCache={emptySourcesContentCache}
            removeSource={() => {}}
            draftConfig={{
              ...emptyDraftConfig,
              templateID:
                selectedTemplate === null ? null : selectedTemplate?.id,
            }}
            setDraftConfig={() => {}}
            onCompleteInputs={onCompleteInputs}
            logComposeEvent={logComposeEvent}
            handleClickAdd={handleClickAdd}
            templateProps={{
              templates: templates || [],
              selectedMessage: templateSelectedMessage,
              setOpenTemplateSettings: setOpenTemplateSettings,
              onTemplateChange,
            }}
          />
        )}
      </>
    );

    const handleStartNewSession = useCallback(() => {
      setEmptyChatOption(undefined);
      setIsConversationPageLoading(true);
      resetNewConversation(() => {
        navigate(RoutePath.CHAT, { replace: true });
      });
      resetDraftConfigState();
      setUserMessageOptions(undefined);
    }, [
      navigate,
      resetNewConversation,
      resetDraftConfigState,
      setIsConversationPageLoading,
    ]);

    // Exists separately from the above because callbacks for mouse events cannot take parameters
    const handleStartNewSessionWithState = useCallback(
      (routerState?: ConversationRouterState) => {
        setEmptyChatOption(undefined);
        setUserMessageOptions(undefined);
        resetDraftConfigState();
        setIsConversationPageLoading(true);
        resetNewConversation(() => {
          navigate(RoutePath.CHAT, { replace: true, state: routerState });
        });
      },
      [
        navigate,
        resetDraftConfigState,
        resetNewConversation,
        setIsConversationPageLoading,
      ],
    );

    const categoryWrapper = useMemo(
      () =>
        firstMessageFromUser ? (
          <CategorySearchAccordionWrapper
            query={firstMessageFromUser}
            hasResults={() => setHasCategoryResults(true)}
            shouldSkipCache={true}
          />
        ) : undefined,
      [firstMessageFromUser, setHasCategoryResults],
    );

    // Reset whether category results are shown if the message changes from the user
    useEffect(() => {
      setHasCategoryResults(false);
    }, [firstMessageFromUser]);

    const sideBarChildren = useMemo(() => {
      if (rightPaneExpanded) return undefined;

      if (stackChatDetails) {
        return (
          <StackChatPanel
            chatSessionId={currentSessionID}
            lastUserMessage={stackChatDetails.lastUserMessage}
            onRefreshConversation={handleStartNewSessionWithState}
            serverConversationId={conversation?.serverConversationId}
            sourceDocumentIds={stackChatDetails.sourceDocumentIds}
            stackSharingId={stackChatDetails.sharingId}
            query={firstMessageFromUser}
          />
        );
      } else if (
        isCategoriesEnabled &&
        firstMessageFromUser &&
        hasCategoryResults
      ) {
        return categoryWrapper;
      } else {
        return undefined;
      }
    }, [
      firstMessageFromUser,
      hasCategoryResults,
      rightPaneExpanded,
      isCategoriesEnabled,
      categoryWrapper,
      handleStartNewSessionWithState,
      stackChatDetails,
    ]);

    if (!isConversationAssistantEnabled) {
      return <Navigate to={RoutePath.CHAT} replace />;
    }

    if (!conversationId) {
      return null;
    }

    const sidePanelButtons = workflowAgentUI?.showWorkflowAgent ? (
      workflowAgentUI.backButton
    ) : (
      <>
        <ConversationList
          showListPreview={showEmptyChatInstructions}
          onOpenTemplates={handleExploreTemplates}
          handleStartNewSessionWithState={handleStartNewSessionWithState}
        />
        <IconButtonWithTooltip
          variant="borderless"
          tooltipProps={{
            title: i18n.t("compose_new_session"),
          }}
          onClick={handleStartNewSession}
        >
          <UIIcon src={AddCircleLine} />
        </IconButtonWithTooltip>
        {state && state.scenarioMeta && (
          <ScenarioBackButton scenario={state.scenarioMeta} />
        )}
      </>
    );

    const chatGrid = (
      <>
        <ChatGrid
          logChatEvent={() => {
            // TODO logging
          }}
          rightPaneExpanded={rightPaneExpanded}
          setExpandRightPane={setRightPaneExpanded}
          sidePane={
            <SidePanelContainer
              leftTitleActions={sidePanelButtons}
              handleClickAdd={handleClickAdd}
              showWorkflowAgent={workflowAgentUI?.showWorkflowAgent ?? false}
              rightTitleActions={workflowAgentUI?.deleteButton}
              sidebarChildren={sideBarChildren}
            >
              {workflowAgentUI?.workflowAgentUI || (
                <ConversationChat
                  variant={variant}
                  startingMessageNode={startingMessageNode}
                  conversationId={conversationId}
                  handleClickAdd={handleClickAdd}
                  handleCloseAddSourcesModal={handleCloseAddSourcesModal}
                  isAddSourceModalOpen={isAddSourceModalOpen}
                  selectedModel={selectedModel}
                  setSelectedModel={setSelectedModel}
                  agentId={agentId}
                  showEmptyChatInstructions={showEmptyChatInstructions}
                  messages={messagesToDisplay}
                  progressString={progressString}
                  isWaitingForResponse={isWaitingForResponse}
                  agentPayload={agentPayload}
                  stackChatMetadata={stackChatDetails}
                  userMessageOptions={userMessageOptions}
                  setUserMessageOptions={setUserMessageOptions}
                  logComposeEvent={logComposeEvent}
                />
              )}
            </SidePanelContainer>
          }
          rightPane={
            conversation?.markdownDraft && rightPaneExpanded ? (
              <ComposeEditorPane
                markdownDraft={conversation?.markdownDraft}
                isWaitingForResponse={false}
                artifacts={[]}
                setMarkdownContent={updateDraft}
                setDraftConfig={() => {}}
                postUpdateDraftWithVoice={onPostMessageWithVoice}
                messagesHistory={[]}
                currentSessionID={""}
                setInputContext={() => {}}
                postUserMessage={() => {}}
                setOpenTemplateSettings={setOpenTemplateSettings}
                isStreaming={isWaitingForResponse}
                currentVoiceID={selectedVoiceID}
                disableSelectionActions
                assistRequestId={conversation?.assistRequestId}
              />
            ) : null
          }
          showToggle={!!conversation?.markdownDraft}
        />
        {templateSettingsModal}
      </>
    );

    return (
      <>
        {isConversationLoading ? <DashSpinnerAutofitContainer /> : chatGrid}
        {!hasCategoryResults && categoryWrapper}
      </>
    );
  },
);

ConversationAssistantPageContent.displayName =
  "ConversationAssistantPageContent";

function useConversationId(searchParams: URLSearchParams) {
  const navigate = useNavigate();
  const { state } = useLocation();

  const [conversationId, setConversationId] = useState<string | undefined>(
    undefined,
  );
  const conversationIdLookupFromSearchParams =
    useConversationIdFromSearchParams(searchParams);

  const resetNewConversation = useCallback(
    (onResetComplete?: () => void) => {
      createNewConversation()
        .then((conversation) => {
          // If a new conversation was created as the result of a stateful navigation, update
          // the URL to point to the newly created conversation ID, replacing the current entry
          // and persisting the router state.
          if (state && "conversationType" in state) {
            navigate(urlForConversationWithID(conversation.id), {
              replace: true,
              state,
            });
          }

          setConversationId(conversation.id);

          onResetComplete?.();

          return;
        })
        .catch((e) => {
          throw e;
        });
    },
    [navigate, setConversationId],
  );

  useEffect(() => {
    if (conversationIdLookupFromSearchParams.state === "loaded") {
      if (conversationIdLookupFromSearchParams.conversationId) {
        setConversationId(conversationIdLookupFromSearchParams.conversationId);
      } else {
        // If there is no conversation id in the search params only if we don't already
        // have a conversation id from another source. This prevents duplicate resets after
        // an explicit call to resetNewConversation.
        if (!conversationId) {
          resetNewConversation();
        }
      }
    }
  }, [conversationIdLookupFromSearchParams, resetNewConversation]);
  return {
    conversationId,
    resetNewConversation,
    isConversationLoading:
      conversationIdLookupFromSearchParams.state === "loading",
  };
}
